import React, { useRef, useEffect, useState, Suspense } from "react";
import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import reactWrapper from "@harbor/elements/utils/react/wrapper";

import * as actions from "../actions";
import AppHoc from "../../../generics/AppHOC";
import { useI18n } from "../../../i18n/useI18n";
import Spinner from "../../../common/Spinner";
import {
  verifyDomainIdentifiers,
  validateIdpFields,
  formatDomainIdentifiers,
  compareObjects
} from "../../../utils/query-utils";
import { IDP_ACTIONS } from "../../../utils/enums";
const ErrorComponent = React.lazy(() =>
  import("../../../common/ErrorComponent")
);
import css from "../accountsMagneticStyle.less";

const [
  HbrInput,
  HbrButton,
  HbrSelect,
  HbrOption,
  HbrHighlight,
   HbrIllustration,
] = reactWrapper([
  "hbr-input",
  "hbr-button",
  "hbr-select",
  "hbr-option",
  "hbr-highlight",
  "hbr-illustration"
]);

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

const mapStateToProps = state => {
  return {
    accounts: state.vanalytics.accounts
  };
};

const DEFAULT_FORM_STATE = {
  idp_issuer_url: "",
  idp_sso_url: "",
  idp_signature_cert_expiry: "",
  default_user_role: "v1;*:basic,o365",
  domain_identifier: "",
  file: "",
  idp_certificate: ""
};

const Saml = props => {
  const { tViews } = useI18n("views");
  const { actions, accounts } = props;
  const { setHideModal, uploadSAML, defineIdpSaml, displaySpinner } = actions;
  const {
    idpData,
    idpAction,
    idpServerData,
    samlData,
    error,
    showSpinner
  } = accounts;
  const [state, setState] = useState({
    ...DEFAULT_FORM_STATE,
    file: idpAction === IDP_ACTIONS.EDIT ? false : ""
  });

  const [samlProps, setSamlProps] = useState({
    audience_url: "",
    consumer_service_url: ""
  });
  const [domainIdentifier, setDomainIdentifier] = useState([]);
  const [domainOptions, setDomainOptions] = useState([]);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [stateChanged, setStateChanged] = useState(false);
  const inputFile = useRef(null);
  const stateRef = useRef(idpServerData);
  const domainIdentifierRef = useRef([]);
  const saDomain = idpData.smartAccountReference
    ? idpData.smartAccountReference.domain_id
    : idpData.domain_id;
  const currentDomainValues = Array.isArray(state.domain_identifier)
    ? state.domain_identifier
    : typeof state.domain_identifier === "string"
    ? state.domain_identifier
        .split(",")
        .map(domain => domain.trim())
        .filter(Boolean)
    : [];

  useEffect(() => {
    if (idpAction === IDP_ACTIONS.EDIT) {
      const stateResult = compareObjects(state, stateRef.current);
      const diResult = compareObjects(domainIdentifier, domainIdentifierRef.current);
      setStateChanged(!stateResult || !diResult);
    }
  }, [domainIdentifier, idpAction, state]);

  useEffect(() => {
    if (idpAction === IDP_ACTIONS.DEFINE) {
      setState(previousState => ({ ...previousState, ...samlData }));
    } else {
      const formattedDomainIdentifiers =
        idpServerData.domain_identifier && idpServerData.domain_identifier.length > 0
          ? formatDomainIdentifiers(idpServerData.domain_identifier)
          : [];

      stateRef.current = idpServerData;
      domainIdentifierRef.current = formattedDomainIdentifiers;
      setDomainIdentifier(formattedDomainIdentifiers);
      setDomainOptions(formattedDomainIdentifiers);
      setSamlProps(idpServerData);
      setState(idpServerData);
    }
  }, [idpAction, idpServerData, samlData]);

  const validateFormFields = () => {
    const formValidations = validateIdpFields(state);
    if (Object.keys(formValidations).length > 0) {
      setFormErrors(formValidations);
      return false;
    }

    //Case for already added invalid domains
    const isError = verifyDomainIdentifiers(state.domain_identifier);
    if (isError) {
      setValidationErrors({
        domain_identifier: true
      });
      return false;
    } else {
      return true;
    }
  };

  const onSave = event => {
    event.preventDefault();
    const isFormValid = validateFormFields();
    if (isFormValid) {
      const requestPayload = {
        default_user_role: state.default_user_role,
        domain_identifier: state.domain_identifier,
        idp_certificate: samlData.idp_certificate,
        idp_issuer_url: state.idp_issuer_url,
        idp_signature_cert_expiry: state.idp_signature_cert_expiry,
        idp_sso_url: state.idp_sso_url,
        idp_type: "SAML",
        overlay_id: idpData.overlay_id,
        sa_domain: saDomain,
        va_id: idpData.va_id
      };

      if (idpAction !== IDP_ACTIONS.DEFINE) {
        requestPayload.idp_id = state.idp_id;
      }
      defineIdpSaml(requestPayload);
      displaySpinner({
        showSpinner: true
      });
    }
  };

  const onClose = () => {
    setHideModal();
  };

  const openFileInput = () => {
    inputFile.current.click();
  };

  const onFileSelect = e => {
    const { value, files } = e.target;
    if (files.length > 0) {
      setState(previousState => ({ ...previousState, file: value }));
      setFormErrors(previousErrors => ({
        ...previousErrors,
        file: value !== "" ? false : true
      }));
      const reader = new FileReader();
      reader.onload = event => {
        const fileContent = event.target.result;
        uploadSAML({
          va_id: idpData.va_id,
          sa_domain: saDomain,
          metadata: fileContent
        });
        displaySpinner({
          showSpinner: true
        });
      };
      reader.readAsText(files[0]);
    }
  };

  const handleDomainChange = event => {
    const value = event.target.value;
    const { option } = event.detail;
    if (option) {
      if (!value.length) {
        setState(values => ({ ...values, domain_identifier: [option.value] }));
      } else if (value.includes(option.value)) {
        const newValue = value.filter((item) => item !== option.value);
        setState(values => ({ ...values, domain_identifier: newValue }));
      } else {
        const newValue = [...value, option.value];
        setState(values => ({ ...values, domain_identifier: newValue }));
      }
      
      // Clear form error for domain_identifier when a selection is made
      setFormErrors(values => ({
        ...values,
        domain_identifier: false
      }));
    }

    if (domainSearchQuery) {
      setDomainSearchQuery("");
      setDomainOptions(domainIdentifier);
    }
  };

  const handleDomainSearch = event => {
    if (!event.detail) {
      setDomainSearchQuery("");
      setDomainOptions(domainIdentifier);
      return;
    }

    const searchValue = event.detail.value || "";
    setDomainSearchQuery(searchValue);

    const filtered = domainIdentifier
      .filter(item => {
        const itemText = item.text.toLowerCase();
        const searchText = searchValue.toLowerCase();
        return itemText.includes(searchText);
      })
      .map(item => ({
        ...item,
        query: searchValue
      }));

    setDomainOptions(filtered);
  };

  const handleAddDomain = event => {
    event?.preventDefault?.();

    const trimmedDomainQuery = domainSearchQuery.trim();
    if (!trimmedDomainQuery) {
      return;
    }

    const newValue = [...currentDomainValues, trimmedDomainQuery];
    const newDomainData = [
      ...domainIdentifier,
      { text: trimmedDomainQuery, value: trimmedDomainQuery }
    ];

    setDomainIdentifier(newDomainData);
    setDomainOptions(newDomainData);
    setState(values => ({ ...values, domain_identifier: newValue }));
    setDomainSearchQuery("");

    const hasInvalidDomains = verifyDomainIdentifiers(newValue);
    setValidationErrors({
      domain_identifier: !!hasInvalidDomains
    });
    setFormErrors(values => ({
      ...values,
      domain_identifier: false
    }));
  };

  const formItemChange = e => {
    let name;
    let value;

    if (e.detail) {
      name = e.target.name;
      value = e.detail.value;
    } else {
      const target = e.target || e.currentTarget;
      name = target.name;
      value = target.value;
    }

    if (name === "domain_identifier") {
      const hasInvalidDomains = verifyDomainIdentifiers(value);
      setValidationErrors({
        [name]: !!hasInvalidDomains
      });
    }

    setState(values => ({ ...values, [name]: value }));
    setFormErrors(values => ({
      ...values,
      [name]: value && value.trim() !== "" ? false : true
    }));
  };

  const downloadIDPMetadata = () => {
    if (idpServerData.saml_metadata) {
      const link = document.createElement("a");
      const blob = new Blob([idpServerData.saml_metadata], {
        type: "text/plain"
      });

      link.setAttribute("href", window.URL.createObjectURL(blob));
      link.setAttribute("download", "saml_metadata.xml");

      link.dataset.downloadurl = ["text/plain", link.download, link.href].join(
        ":"
      );
      link.draggable = true;
      link.classList.add("dragout");

      link.click();
    }
  };

  return (
    <>
      {error ? (
        <Suspense fallback={<Spinner />}>
          <ErrorComponent className={"large-dashlet-error"} {...error} />
        </Suspense>
      ) : (
        <form className={css["saml-form"]} onSubmit={onSave}>
          {showSpinner && <Spinner />}
          {idpAction === IDP_ACTIONS.DEFINE && (
            <>
              <div className="link-heading">{tViews("idpMetadata")}</div>
              <HbrButton
                name="fileBtn"
                variant="text"
                onClick={openFileInput}
                size="small"
                className="saml-metadata"
              >
                {tViews("idpConfigBrowseFile")}
              </HbrButton>
              <p className="field-error">
                {formErrors.file ? tViews("idpConfigInvalidXml") : ""}
              </p>
              <input
                type="file"
                id="file"
                name="file"
                ref={inputFile}
                onChange={onFileSelect}
                style={{ display: "none" }}
              />
            </>
          )}
          <HbrInput
            maxwidth="100%"
            size="medium"
            name="idp_issuer_url"
            label={tViews("samlIdpIssuerUrl")}
            placeholder={tViews("samlIdpIssuerUrl")}
            value={state.idp_issuer_url}
            onHbr-input={formItemChange}
            disabled={true}
            required={true}
            error={!!formErrors.idp_issuer_url}
          >
            {formErrors.idp_issuer_url && (
              <div slot="help-text" className="error-text">
                {tViews("idpConfigFieldRequired")}
              </div>
            )}
          </HbrInput>

          <HbrInput
            maxwidth="100%"
            size="medium"
            name="idp_sso_url"
            label={tViews("samlIdpSingleSignOnUrl")}
            placeholder={tViews("samlIdpSingleSignOnUrl")}
            value={state.idp_sso_url}
            onHbr-input={formItemChange}
            disabled={true}
            required={true}
            error={!!formErrors.idp_sso_url}
          >
            {formErrors.idp_sso_url && (
              <div slot="help-text" className="error-text">
                {tViews("idpConfigFieldRequired")}
              </div>
            )}
          </HbrInput>

          <HbrInput
            maxwidth="100%"
            size="medium"
            name="idp_signature_cert_expiry"
            label={tViews("samlIdpSignatureCertExpiry")}
            placeholder={tViews("samlIdpSignatureCertExpiry")}
            value={state.idp_signature_cert_expiry}
            onHbr-input={formItemChange}
            disabled={true}
            required={true}
            error={!!formErrors.idp_signature_cert_expiry}
          >
            {formErrors.idp_signature_cert_expiry && (
              <div slot="help-text" className="error-text">
                {tViews("idpConfigFieldRequired")}
              </div>
            )}
          </HbrInput>
          {idpAction !== IDP_ACTIONS.DEFINE && (
            <div className="saml-metadata">
              <div className="link-heading">{tViews("samlMetadata")}</div>
              <HbrButton
                name="downloadIdp"
                variant="text"
                size="small"
                onClick={downloadIDPMetadata}
              >
                {tViews("downloadIdpMetadata")}
              </HbrButton>
            </div>
          )}

          {idpAction === IDP_ACTIONS.VIEW && (
            <>
              <HbrInput
                maxwidth="100%"
                size="medium"
                name="consumer_service_url"
                label={tViews("samlAssertionConsumerServiceUrl")}
                placeholder={tViews("samlAssertionConsumerServiceUrl")}
                value={samlProps.consumer_service_url}
                onHbr-input={formItemChange}
                disabled={true}
              ></HbrInput>

              <HbrInput
                maxwidth="100%"
                size="medium"
                name="audience_url"
                label={tViews("samlAudienceUrl")}
                placeholder={tViews("samlAudienceUrl")}
                value={samlProps.audience_url}
                onHbr-input={formItemChange}
                disabled={true}
              ></HbrInput>
            </>
          )}

          <HbrInput
            maxwidth="100%"
            size="medium"
            name="default_user_role"
            label={tViews("oidcDefaultUserRole")}
            placeholder={tViews("oidcDefaultUserRole")}
            value={state.default_user_role}
            onHbr-input={formItemChange}
            disabled={idpAction === IDP_ACTIONS.VIEW}
            required={true}
            error={!!formErrors.default_user_role}
          >
            {formErrors.default_user_role && (
              <div slot="help-text" className="error-text">
                {tViews("idpConfigFieldRequired")}
              </div>
            )}
          </HbrInput>

          <HbrSelect
            maxwidth="100%"
            name="domain_identifier"
            placeholder={
              idpAction === IDP_ACTIONS.DEFINE
                ? tViews("samlAddDomainIdentifier")
                : tViews("oidcDomainIdentifier")
            }
            onHbr-change={handleDomainChange}
            label={tViews("oidcDomainIdentifier")}
            searchable={true}
            onHbr-search={handleDomainSearch}
            onHbr-clear={() => {
              setState(values => ({ ...values, domain_identifier: [] }));
              setDomainSearchQuery("");
              setDomainOptions(domainIdentifier);
            }}
            clearable={true}
            variant="tag"
            disabled={idpAction === IDP_ACTIONS.VIEW}
            required
            value={currentDomainValues}
            error={!!(formErrors.domain_identifier || validationErrors.domain_identifier)}
          >
            {(formErrors.domain_identifier || validationErrors.domain_identifier) && (
              <div slot="help-text" className="error-text">
                {formErrors.domain_identifier
                  ? tViews("idpConfigFieldRequired")
                  : validationErrors.domain_identifier
                  ? tViews("idpConfigInvalidDomains")
                  : ""}
              </div>
            )}
            {domainOptions.length ? (
              domainOptions.map((option, idx) => (
                <HbrOption value={option.value} key={idx.toString()} allowSpace={true}>
                  {domainSearchQuery ? (
                    <HbrHighlight
                      text={option.text}
                      queryText={option.query}
                      continuous={false}
                    />
                  ) : (
                    option.text
                  )}
                </HbrOption>
              ))
            ) : (
              <>
                <div className="hbr-css__layout-row hbr-css__layout-justify-center no-matches-section">
                  <HbrIllustration size="small">
                    <div slot="body" className="hbr-css__text-primary-xs">
                      No matches found.
                    </div>
                  </HbrIllustration>
                </div>
                {domainSearchQuery && (
                  <div className="add-domain-section">
                    <span>&quot;{domainSearchQuery}&quot;</span>
                    <HbrButton
                      variant="primary"
                      size="small"
                      type="button"
                      onClick={handleAddDomain}
                    >
                      + Add
                    </HbrButton>
                  </div>
                )}
              </>
            )}
          </HbrSelect>
          <div className="cta-bar">
            {idpAction !== IDP_ACTIONS.VIEW && (
              <>
                <HbrButton
                  name="formUpdate"
                  variant="link"
                  type="button"
                  onClick={onClose}
                >
                  {tViews("idpConfigCancel")}
                </HbrButton>
                <HbrButton
                  name="formSubmit"
                  type="submit"
                  disabled={idpAction === IDP_ACTIONS.EDIT && !stateChanged}
                >
                  {tViews("idpConfigSave")}
                </HbrButton>
              </>
            )}
          </div>
        </form>
      )}
    </>
  );
};

Saml.propTypes = {
  actions: PropTypes.object.isRequired,
  accounts: PropTypes.object.isRequired
};

export default (
  connect(mapStateToProps, mapDispatchToProps)(AppHoc(Saml))
);
